import { EventEmitter } from 'events';

/**
 * Enhanced State Manager with caching, invalidation, and event system
 */
export class StateManager extends EventEmitter {
  constructor() {
    super();
    this.cache = new Map();
    this.loadingStates = new Map();
    this.lastFetch = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes default cache
  }

  /**
   * Get cached data or trigger fetch if needed
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Function to fetch data
   * @param {Object} options - Options for caching
   * @returns {Promise<any>} Cached or fresh data
   */
  async get(key, fetchFn, options = {}) {
    const {
      forceRefresh = false,
      cacheTimeout = this.cacheTimeout,
      onError = null
    } = options;

    // Check if we're already loading this key
    if (this.loadingStates.has(key)) {
      return this.loadingStates.get(key);
    }

    // Check cache validity
    const cached = this.cache.get(key);
    const lastFetchTime = this.lastFetch.get(key);
    const isCacheValid = cached && lastFetchTime && 
      (Date.now() - lastFetchTime) < cacheTimeout;

    if (!forceRefresh && isCacheValid) {
      return cached;
    }

    // Create loading promise
    const loadingPromise = this._fetchWithErrorHandling(key, fetchFn, onError);
    this.loadingStates.set(key, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(key, data);
      this.lastFetch.set(key, Date.now());
      this.emit('dataUpdated', { key, data });
      return data;
    } finally {
      this.loadingStates.delete(key);
    }
  }

  /**
   * Fetch data with error handling
   * @private
   */
  async _fetchWithErrorHandling(key, fetchFn, onError) {
    try {
      return await fetchFn();
    } catch (error) {
      this.emit('error', { key, error });
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  /**
   * Invalidate cache for a specific key
   * @param {string} key - Cache key to invalidate
   */
  invalidate(key) {
    this.cache.delete(key);
    this.lastFetch.delete(key);
    this.emit('cacheInvalidated', { key });
  }

  /**
   * Invalidate all cache
   */
  invalidateAll() {
    this.cache.clear();
    this.lastFetch.clear();
    this.emit('cacheInvalidated', { key: 'all' });
  }

  /**
   * Set data directly in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  set(key, data) {
    this.cache.set(key, data);
    this.lastFetch.set(key, Date.now());
    this.emit('dataUpdated', { key, data });
  }

  /**
   * Get cached data without triggering fetch
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null
   */
  getCached(key) {
    return this.cache.get(key) || null;
  }

  /**
   * Check if data is currently loading
   * @param {string} key - Cache key
   * @returns {boolean} True if loading
   */
  isLoading(key) {
    return this.loadingStates.has(key);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      loadingCount: this.loadingStates.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Clear all data and loading states
   */
  clear() {
    this.cache.clear();
    this.loadingStates.clear();
    this.lastFetch.clear();
    this.emit('cleared');
  }
}
